import {castArray, omit} from 'lodash';
import {useCallback, useContext, useMemo, useState} from 'react';

import {AddButton} from '../../../../../../layout/add-button/add-button';
import {AdditionalInfo} from '../../../../../../layout/additional-info/additional-info';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../../provider/main-filter/main-filter-type';
import {BulkEditFieldNameEnum, CompanyErrorsType, FieldModeEnum} from '../../../../../../service/company/company-type';
import {usePhoneDescriptionEnabledForUserBrands} from '../../../../../../service/feature-flags/use-phone-description-enabled';
import {useFormRules} from '../../../../../../service/form/form-rules-hook';
import {usePhoneCommentVisibility} from '../../../../../../service/phone-comment-visibility/phone-comment-visibility-hook';
import {getRandomString, splitCommaSeparatedString} from '../../../../../../util/string';
import {useUrl} from '../../../../../../util/url-hook/url-hook';
import {SELECTED_ALL} from '../../../../my-companies/companies-table/companies-table-const';
import {serializeBulkEditCompaniesFilters} from '../../../bulk-edit-companies-helper';
import {BulkEditPhoneErrorType, BulkEditSearchParametersEnum} from '../../../bulk-edit-companies-type';
import {BaseCompanyFormItemPropsType} from '../company-form-items-type';

import {InputString} from './input-string/input-string';
import {InputStringType} from './input-string/input-string-type';
import {ValidatePhoneNumberType} from './phones-form-item-type';
import * as styles from './phones-form-item.scss';

type PropsType = BaseCompanyFormItemPropsType<BulkEditFieldNameEnum.phones> & {
    containerClassName?: string;
    addButtonClassName?: string;
    hasError?: boolean;
    inputClassName?: string;
    mode?: FieldModeEnum;
    serverError?: CompanyErrorsType['phones'];
    helpText?: JSX.Element;
};

type RowErrorsType = {
    phone: Array<string>;
    description: Array<string>;
};

const EMPTY_ROW_ERRORS: RowErrorsType = {phone: [], description: []};

function getRowErrors(errorMessage: PropsType['errorMessage'], index: number): RowErrorsType {
    if (!errorMessage || !Array.isArray(errorMessage)) {
        return EMPTY_ROW_ERRORS;
    }

    const rowError: BulkEditPhoneErrorType | Array<string> | string | null = errorMessage[index] ?? null;

    if (!rowError) {
        return EMPTY_ROW_ERRORS;
    }

    if (typeof rowError === 'string') {
        return {phone: [rowError], description: []};
    }

    if (Array.isArray(rowError)) {
        return {phone: rowError, description: []};
    }

    return {phone: rowError.phone || [], description: rowError.description || []};
}

export function PhonesFormItem(props: PropsType): JSX.Element {
    const {
        value: phones,
        onChange,
        containerClassName,
        addButtonClassName,
        hasError,
        mode = FieldModeEnum.replacement,
        inputClassName,
        serverError,
        errorMessage,
        helpText,
    } = props;

    const {validatePhoneNumber} = useFormRules();
    const {getLocalizedString} = useLocale();
    const {getQuery} = useUrl();
    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    // The same selection the page itself submits: explicit ids, or the top-filter reference plus
    // exclusions when "select all" is on. Asking with it keeps the field in sync with what a save
    // would actually touch.
    const selectedRawValue = getQuery(BulkEditSearchParametersEnum.SelectedIds) || '';
    const excludedRawValue = getQuery(BulkEditSearchParametersEnum.ExcludedIds) || '';
    const bulkEditFilter = useMemo(() => {
        const companyIds =
            selectedRawValue === SELECTED_ALL ? [] : splitCommaSeparatedString(selectedRawValue).map(Number);

        const filter = serializeBulkEditCompaniesFilters(
            companyIds,
            splitCommaSeparatedString(excludedRawValue).map(Number),
            mainFilterKey
        );

        // The top filter yields a key only while something is applied in it. A blank one is not a
        // filter, so drop the field rather than sending it empty.
        return 'filter_key' in filter && !filter.filter_key ? omit(filter, 'filter_key') : filter;
    }, [selectedRawValue, excludedRawValue, mainFilterKey]);

    const isFeatureAvailable = usePhoneDescriptionEnabledForUserBrands();

    const {data: phoneCommentVisibility} = usePhoneCommentVisibility(
        isFeatureAvailable ? {filter: bulkEditFilter} : null
    );

    // Past the flag, the backend is the only source of truth for this field: whether the selection
    // forwards the comment at all, and whether it resolves to any company. Nothing is guessed locally.
    const hasDescription = phoneCommentVisibility?.isVisible === true;

    const isDisabledAddButton = useMemo(() => {
        return phones[0]?.value.length === 0;
    }, [phones]);

    const [errors, setErrors] = useState<ValidatePhoneNumberType>({});
    const handleChange = useCallback(
        (itemList: Array<InputStringType>) => {
            const validateErrors: Record<string, boolean> = {};

            itemList.forEach((item: InputStringType) => {
                const {value, id} = item;

                if (value.length > 0) {
                    try {
                        const isValid = validatePhoneNumber(value);

                        validateErrors[id] = !isValid;
                    } catch {
                        validateErrors[id] = true;
                    }
                }
            });

            setErrors(validateErrors);
            onChange(itemList);
        },
        [onChange, validatePhoneNumber]
    );

    const phoneLabel = (
        <AdditionalInfo
            leftNode={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__LABEL" />}
            title={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__LABEL" />}
        >
            <Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__ADDITIONAL_INFO" />
        </AdditionalInfo>
    );

    const descriptionLabel = (
        <AdditionalInfo
            leftNode={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__LABEL" />}
            title={<Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__LABEL" />}
        >
            <Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__ADDITIONAL_INFO" />
        </AdditionalInfo>
    );

    // TODO: after new design and refactoring there should be only one 'errorMessage' prop
    function getErrorMessages(index: number): RowErrorsType {
        const serverRowError = serverError && serverError[index];

        if (serverRowError) {
            return {phone: serverRowError.phone || [], description: serverRowError.description || []};
        }

        const rowErrors = getRowErrors(errorMessage, index);

        return {phone: castArray(rowErrors.phone), description: rowErrors.description};
    }

    const phoneRows = phones.map((phone: InputStringType, index: number): JSX.Element => {
        const {phone: phoneErrorMessages, description: descriptionErrorMessages} = getErrorMessages(index);

        return (
            <InputString
                // The main-phone hint moves into the star popover: the right slot shows the sending hint.
                additionalInfo={{
                    title: <Locale stringKey="CONTACTS__INFO__MAIN_PHONE_NUMBER" />,
                    content: hasDescription ? (
                        <Locale stringKey="BULK_EDIT_COMPANIES__PHONE_FIELD_ADDITIONAL_INFO" />
                    ) : (
                        <Locale stringKey="CONTACTS__INFO__MAIN_PHONE_NUMBER__CONTENT" />
                    ),
                }}
                addonBefore=""
                className={inputClassName}
                descriptionErrorMessages={descriptionErrorMessages}
                descriptionLabel={index === 0 ? descriptionLabel : null}
                descriptionPlaceholder={getLocalizedString(
                    'COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__PLACEHOLDER'
                )}
                errorMessages={phoneErrorMessages}
                hasDescription={hasDescription}
                hasError={(index === 0 && hasError) || phoneErrorMessages.length > 0}
                hasMainItem={mode === FieldModeEnum.replacement}
                hasPhoneValidateError={errors[phone.id]}
                helpText={index === 0 && !hasDescription ? helpText : null}
                isRequired
                item={phone}
                itemList={phones}
                key={phone.id}
                label={index === 0 && hasDescription ? phoneLabel : null}
                placeholder={getLocalizedString(
                    hasDescription
                        ? 'COMPANY_FORM__CONTACT_INFO__PHONE__PLACEHOLDER'
                        : 'CONTACTS__PLACEHOLDER__PHONE_NUMBERS'
                )}
                renderRemovedItemText={(value) => (
                    <Locale stringKey="CONTACTS__INFO__REMOVED_PHONE" valueMap={{value}} />
                )}
                setItemList={handleChange}
            />
        );
    });

    return (
        <div className={containerClassName}>
            {hasDescription ? (
                <div className={styles.phones_block_with_hint}>
                    <div className={styles.phones_block__rows}>{phoneRows}</div>

                    <div className={styles.phones_block__hint}>
                        <Locale stringKey="COMPANY_FORM__CONTACT_INFO__PHONE__DESCRIPTION__SENDING_HINT" />
                    </div>
                </div>
            ) : (
                phoneRows
            )}

            <AddButton
                className={addButtonClassName}
                isDisabled={isDisabledAddButton}
                onClick={() =>
                    onChange([
                        ...phones,
                        hasDescription
                            ? {value: '', id: getRandomString(), description: ''}
                            : {value: '', id: getRandomString()},
                    ])
                }
                type="link"
            >
                <Locale stringKey="BUTTON__ADD" />
            </AddButton>
        </div>
    );
}
